// client/src/components/CRMInboundForm.jsx
// Merged inbox-intake form: combines LogOpportunity wizard structure with
// FeederIntakeForm classification fields. Replaces both predecessors.
//
// Step 1: Company (search or inline create)
// Step 2: Project details (name + type + conditional start date)
// Step 3: Contact (search or inline create, with caller_* fields auto-derived)
// Step 4: Intake details (notes + how-heard)
//
// On submit: POST /api/crm/inbox with contact_id, opening_notes, description,
// project_type, planned_start_date, how_heard, and caller_* fields derived
// from the selected contact (Option B).

import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../api';
import T from './crmTheme';

const HOW_HEARD_OPTIONS = [
  { value: '',          label: '-- Select --' },
  { value: 'website',   label: 'Website' },
  { value: 'referral',  label: 'Referral' },
  { value: 'linkedin',  label: 'LinkedIn' },
  { value: 'cold_call', label: 'Cold Call' },
  { value: 'event',     label: 'Event' },
  { value: 'other',     label: 'Other' },
];

const PROJECT_TYPE_OPTIONS = [
  { value: '',             label: '-- Select --' },
  { value: 'construction', label: 'Construction' },
  { value: 'consultancy',  label: 'Consultancy' },
  { value: 'surveying',    label: 'Surveying' },
];

const STEP_LABELS = ['Company', 'Project Details', 'Contact', 'Intake Details'];

function CRMInboundForm({ isOpen, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Step 1: Company
  const [companySearch, setCompanySearch] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: '', type: '' });

  // Step 2: Project
  const [projectName, setProjectName] = useState('');
  const [projectType, setProjectType] = useState('');
  const [plannedStartDate, setPlannedStartDate] = useState('');

  // Step 3: Contact
  const [contactSearch, setContactSearch] = useState('');
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState({ first_name: '', last_name: '', email: '', phone: '' });

  // Step 4: Intake
  const [notes, setNotes] = useState('');
  const [howHeard, setHowHeard] = useState('');

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setError('');
      setSelectedCompany(null);
      setSelectedContact(null);
      setShowNewCompany(false);
      setShowNewContact(false);
      setCompanySearch('');
      setContactSearch('');
      setNewCompany({ name: '', type: '' });
      setNewContact({ first_name: '', last_name: '', email: '', phone: '' });
      setProjectName('');
      setProjectType('');
      setPlannedStartDate('');
      setNotes('');
      setHowHeard('');
    }
  }, [isOpen]);

  // Debounced company search
  useEffect(() => {
    if (companySearch.length < 2) { setCompanies([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.crmCompanies(companySearch);
        setCompanies(res.slice(0, 8));
      } catch (e) { /* silent */ }
    }, 300);
    return () => clearTimeout(t);
  }, [companySearch]);

  // Debounced contact search (filtered by selected company)
  useEffect(() => {
    if (!selectedCompany) return;
    const t = setTimeout(async () => {
      try {
        const res = await api.crmContacts({ company_id: selectedCompany.id, search: contactSearch });
        setContacts(res.slice(0, 8));
      } catch (e) { /* silent */ }
    }, 300);
    return () => clearTimeout(t);
  }, [contactSearch, selectedCompany]);

  // Validation per step
  const canAdvance = useCallback(() => {
    if (step === 1) {
      if (showNewCompany) return newCompany.name.trim().length > 0;
      return !!selectedCompany;
    }
    if (step === 2) {
      return projectName.trim().length > 0;
    }
    if (step === 3) {
      if (showNewContact) {
        const hasName = newContact.first_name.trim() && newContact.last_name.trim();
        const hasContact = newContact.email.trim() || newContact.phone.trim();
        return hasName && hasContact;
      }
      return !!selectedContact;
    }
    return true;
  }, [step, showNewCompany, newCompany, selectedCompany, projectName, showNewContact, newContact, selectedContact]);

  const advance = () => {
    if (!canAdvance()) {
      setError('Please complete the required fields before continuing.');
      return;
    }
    setError('');
    setStep(s => s + 1);
  };

  const back = () => {
    setError('');
    setStep(s => Math.max(1, s - 1));
  };

  // Submit
  const submit = async () => {
    setError('');
    setSubmitting(true);
    try {
      // Resolve company_id (create if new)
      let company_id = selectedCompany?.id;
      if (showNewCompany && !selectedCompany) {
        const co = await api.crmCreateCompany({
          name: newCompany.name.trim(),
          company_type: newCompany.type || null,
        });
        company_id = co.id;
      }
      if (!company_id) throw new Error('Company is required');

      // Resolve contact_id (create if new)
      let contact = selectedContact;
      if (showNewContact && !selectedContact) {
        contact = await api.crmCreateContact({
          first_name: newContact.first_name.trim(),
          last_name: newContact.last_name.trim(),
          email: newContact.email.trim() || null,
          phone: newContact.phone.trim() || null,
          company_id,
        });
      }
      if (!contact?.id) throw new Error('Contact is required');

      // Derive caller_* fields from contact (Option B: auto-populate)
      const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim();

      // Build payload - notes write to both description and opening_notes
      const payload = {
        title: projectName.trim(),
        company_id,
        contact_id: contact.id,
        description: notes.trim() || null,
        opening_notes: notes.trim() || null,
        caller_name: fullName || null,
        caller_phone: contact.phone || null,
        caller_email: contact.email || null,
        urgency: 'normal',
        how_heard: howHeard || null,
        project_type: projectType || null,
        planned_start_date: plannedStartDate || null,
      };

      await api.crmCreateInbox(payload);
      onClose();
      if (onCreated) onCreated();
    } catch (e) {
      setError(e.message || 'Failed to log inbound enquiry');
    }
    setSubmitting(false);
  };

  if (!isOpen) return null;

  // Styles
  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const modal = {
    background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12,
    padding: 28, width: 520, maxHeight: '85vh', overflowY: 'auto',
    boxShadow: T.shadow || '0 20px 60px rgba(0,0,0,0.4)',
  };
  const label = {
    fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.06em', color: T.textMuted, display: 'block', marginBottom: 5,
  };
  const input = {
    width: '100%', padding: '8px 10px', background: T.inputBg,
    border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 13,
    fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
    color: T.text,
  };
  const primaryBtn = {
    padding: '8px 16px', background: T.teal, border: 'none', borderRadius: 7,
    color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6,
  };
  const secondaryBtn = {
    padding: '8px 16px', background: T.surface, border: `1px solid ${T.border}`,
    borderRadius: 7, color: T.textMuted, fontSize: 13, cursor: 'pointer',
    fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6,
  };
  const linkBtn = {
    background: 'none', border: 'none', color: T.teal, cursor: 'pointer',
    fontSize: 12, padding: 0, textDecoration: 'underline', fontFamily: 'inherit',
  };
  const listItem = {
    padding: '8px 10px', background: T.surface, border: `1px solid ${T.border}`,
    borderRadius: 6, marginBottom: 4, cursor: 'pointer', fontSize: 13, color: T.text,
  };
  const listItemSelected = {
    ...listItem, background: T.tealDim, borderColor: T.teal,
  };

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div style={modal}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: T.text }}>Log Inbound Enquiry</h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: T.textMuted }}>
              Step {step} of 4 &mdash; {STEP_LABELS[step - 1]}
            </p>
          </div>
          <button onClick={onClose} disabled={submitting}
            style={{ background: 'none', border: 'none', cursor: submitting ? 'not-allowed' : 'pointer',
                     color: T.textMuted, padding: 0, lineHeight: 1 }}>
            <X size={20} />
          </button>
        </div>

        {/* Progress indicator */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
          {[1, 2, 3, 4].map(n => (
            <div key={n} style={{
              flex: 1, height: 3, borderRadius: 2,
              background: n <= step ? T.teal : T.border,
            }} />
          ))}
        </div>

        {error && (
          <div style={{ marginBottom: 14, padding: '8px 10px', background: 'rgba(239,68,68,0.1)',
                        border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#fca5a5', fontSize: 12 }}>
            {error}
          </div>
        )}

        {/* Step 1: Company */}
        {step === 1 && (
          <div>
            <label style={label}>Company *</label>
            {!showNewCompany ? (
              <>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: '50%',
                                              transform: 'translateY(-50%)', color: T.textMuted }} />
                  <input value={companySearch}
                    onChange={e => { setCompanySearch(e.target.value); setSelectedCompany(null); }}
                    placeholder="Search company name..." autoFocus
                    style={{ ...input, paddingLeft: 32 }} />
                </div>
                {companies.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    {companies.map(c => (
                      <div key={c.id}
                        onClick={() => { setSelectedCompany(c); setCompanySearch(c.name); setCompanies([]); }}
                        style={selectedCompany?.id === c.id ? listItemSelected : listItem}>
                        {c.name} {c.company_type ? <span style={{ color: T.textMuted }}>&mdash; {c.company_type}</span> : null}
                      </div>
                    ))}
                  </div>
                )}
                {selectedCompany && (
                  <div style={{ marginBottom: 8, fontSize: 12, color: T.teal }}>
                    Selected: {selectedCompany.name}
                  </div>
                )}
                <button onClick={() => { setShowNewCompany(true); setSelectedCompany(null); }} style={linkBtn}>
                  + Company not found? Add new
                </button>
              </>
            ) : (
              <>
                <input value={newCompany.name}
                  onChange={e => setNewCompany(n => ({ ...n, name: e.target.value }))}
                  placeholder="Company name *" autoFocus style={{ ...input, marginBottom: 8 }} />
                <input value={newCompany.type}
                  onChange={e => setNewCompany(n => ({ ...n, type: e.target.value }))}
                  placeholder="Type (e.g. End Client, Contractor) - optional" style={{ ...input, marginBottom: 8 }} />
                <button onClick={() => { setShowNewCompany(false); setNewCompany({ name: '', type: '' }); }} style={linkBtn}>
                  &larr; Search existing companies instead
                </button>
              </>
            )}
          </div>
        )}

        {/* Step 2: Project Details */}
        {step === 2 && (
          <div>
            <label style={label}>Project Name *</label>
            <input value={projectName} onChange={e => setProjectName(e.target.value)}
              placeholder="e.g. Warehouse EV Charging - Manchester" autoFocus
              style={{ ...input, marginBottom: 14 }} />

            <label style={label}>Project Type</label>
            <select value={projectType} onChange={e => setProjectType(e.target.value)}
              style={{ ...input, marginBottom: 14 }}>
              {PROJECT_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {projectType && (
              <>
                <label style={label}>
                  {projectType === 'consultancy' ? 'When Services Required' : 'Projected Onsite Start Date'}
                </label>
                <input type="date" value={plannedStartDate}
                  onChange={e => setPlannedStartDate(e.target.value)} style={input} />
              </>
            )}
          </div>
        )}

        {/* Step 3: Contact */}
        {step === 3 && (
          <div>
            <label style={label}>Contact at {selectedCompany?.name || 'company'} *</label>
            {!showNewContact ? (
              <>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: '50%',
                                              transform: 'translateY(-50%)', color: T.textMuted }} />
                  <input value={contactSearch}
                    onChange={e => { setContactSearch(e.target.value); setSelectedContact(null); }}
                    placeholder="Search contacts at this company..." autoFocus
                    style={{ ...input, paddingLeft: 32 }} />
                </div>
                {contacts.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    {contacts.map(c => {
                      const name = [c.first_name, c.last_name].filter(Boolean).join(' ');
                      return (
                        <div key={c.id}
                          onClick={() => { setSelectedContact(c); setContactSearch(name); setContacts([]); }}
                          style={selectedContact?.id === c.id ? listItemSelected : listItem}>
                          {name}
                          {c.email ? <span style={{ color: T.textMuted, marginLeft: 6 }}>&mdash; {c.email}</span> : null}
                        </div>
                      );
                    })}
                  </div>
                )}
                {selectedContact && (
                  <div style={{ marginBottom: 8, fontSize: 12, color: T.teal }}>
                    Selected: {[selectedContact.first_name, selectedContact.last_name].filter(Boolean).join(' ')}
                  </div>
                )}
                <button onClick={() => { setShowNewContact(true); setSelectedContact(null); }} style={linkBtn}>
                  + Contact not found? Add new
                </button>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input value={newContact.first_name}
                    onChange={e => setNewContact(n => ({ ...n, first_name: e.target.value }))}
                    placeholder="First name *" autoFocus style={{ ...input, flex: 1 }} />
                  <input value={newContact.last_name}
                    onChange={e => setNewContact(n => ({ ...n, last_name: e.target.value }))}
                    placeholder="Last name *" style={{ ...input, flex: 1 }} />
                </div>
                <input type="email" value={newContact.email}
                  onChange={e => setNewContact(n => ({ ...n, email: e.target.value }))}
                  placeholder="Email (email or phone required)" style={{ ...input, marginBottom: 8 }} />
                <input value={newContact.phone}
                  onChange={e => setNewContact(n => ({ ...n, phone: e.target.value }))}
                  placeholder="Phone (email or phone required)" style={{ ...input, marginBottom: 8 }} />
                <button onClick={() => { setShowNewContact(false); setNewContact({ first_name: '', last_name: '', email: '', phone: '' }); }} style={linkBtn}>
                  &larr; Search existing contacts instead
                </button>
              </>
            )}
          </div>
        )}

        {/* Step 4: Intake Details */}
        {step === 4 && (
          <div>
            <label style={label}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Any additional context - urgency, scope, how the caller described the need..."
              rows={5} style={{ ...input, marginBottom: 14, resize: 'vertical', minHeight: 100 }} />

            <label style={label}>How did they hear about us?</label>
            <select value={howHeard} onChange={e => setHowHeard(e.target.value)} style={input}>
              {HOW_HEARD_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            <div style={{ marginTop: 18, padding: 12, background: T.surface,
                          border: `1px solid ${T.border}`, borderRadius: 7, fontSize: 12, color: T.textDim }}>
              <strong style={{ color: T.text }}>Ready to submit.</strong> This enquiry will land in
              the inbox for BD/Manager review. You can still edit or discard it before it&apos;s promoted.
            </div>
          </div>
        )}

        {/* Footer: nav buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 22, gap: 8 }}>
          <button onClick={back} disabled={step === 1 || submitting} style={{
            ...secondaryBtn,
            visibility: step === 1 ? 'hidden' : 'visible',
            opacity: submitting ? 0.5 : 1,
          }}>
            <ChevronLeft size={14} /> Back
          </button>

          {step < 4 && (
            <button onClick={advance} style={primaryBtn}>
              Next <ChevronRight size={14} />
            </button>
          )}
          {step === 4 && (
            <button onClick={submit} disabled={submitting} style={{
              ...primaryBtn,
              opacity: submitting ? 0.5 : 1,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}>
              {submitting ? 'Submitting...' : 'Log Enquiry'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CRMInboundForm;
